import React, { useEffect, useState } from 'react';
import ActivitySection from './ActivitySection';
import DocumentHistory from './DocumentHistory';
import { openDocument, openDocumentErrorMessage } from '../../overview/cells';
import SectionState from './SectionState';
import { AxisPanel, ScoreReadout } from './ScoreReadout';
import {
  NO_FEEDBACK_SCORE,
  choiceLabel, dayMonth, documentTypeLabel, moveState, plural, timesAsked,
} from './lib/activityFormat';
import { scrollToSection } from './lib/scrollToSection';
import ChatSection from './ChatSection';

const OfferCheck = ({ choice }) => {
  if (choice.offer_check === 'meets') {
    return <span className="td-act-pill td-act-pill--g">Predicted grades meet it</span>;
  }
  if (choice.offer_check === 'short') {
    const short = choice.grades_short != null ? ` · ${plural(choice.grades_short, 'grade')} short` : ' · short';
    return <span className="td-act-pill td-act-pill--a">{`Predicted ${choice.predicted}${short}`}</span>;
  }
  return (
    <span className="td-act-pill td-act-pill--o">
      {choice.predicted ? `Predicted ${choice.predicted} · cannot compare` : 'No predicted grades to compare'}
    </span>
  );
};

const Choice = ({ choice, onJump }) => (
  <div className="td-act-el">
    <div className="td-act-el__head">
      <b>{`${choice.institution} · ${choice.course}`}</b>
      <span className={`td-act-pill td-act-pill--${choice.choice === 'other' ? 'o' : 'b'}`}>
        {choiceLabel(choice.choice) || 'Choice'}
      </span>
    </div>
    <div className="td-act-req">
      <span>{choice.typical_offer ? `Typical offer ${choice.typical_offer}` : 'No typical offer recorded'}</span>
      <OfferCheck choice={choice} />
      {choice.admissions_tests.map((t) => (
        <React.Fragment key={t.exam}>
          <span>{`Admissions test: ${t.exam}`}</span>
          {t.practised
            ? (
              <button type="button" className="td-linkbtn" onClick={() => onJump('exams')}>
                {`See ${t.exam} practice →`}
              </button>
            )
            : <span className="td-act-meta">Not practised in this period</span>}
        </React.Fragment>
      ))}
    </div>
  </div>
);

const Moves = ({ moves }) => (
  <ul className="td-act-moves">
    {moves.map((m) => {
      const state = moveState(m.state);
      return (
        <li key={m.label} className="td-act-move">
          <span className={`td-act-move__mark td-act-move__mark--${state ? state.tone : 'part'}`} aria-hidden="true">
            {state ? state.symbol : '·'}
          </span>
          <span className="td-act-sr">{`${state ? state.text : 'Unknown'}: `}</span>
          {m.label}
        </li>
      );
    })}
  </ul>
);

function documentMeta(doc) {
  const earlier = doc.earlier_versions > 0 ? ` · ${plural(doc.earlier_versions, 'earlier version')} kept` : '';
  if (doc.current_version == null) return `Not started${earlier}`;
  const saved = doc.last_saved_at ? ` saved ${dayMonth(doc.last_saved_at)}` : '';
  return `Version ${doc.current_version}${saved} · feedback asked for ${timesAsked(doc.feedback_requests)}${earlier}`;
}

const Element = ({ element, showHead }) => (
  <div className="td-act-el">
    {showHead ? (
      <div className="td-act-el__head">
        <b>{element.label}</b>
        <span className="td-act-meta">
          {element.last_feedback_at
            ? `Last feedback ${dayMonth(element.last_feedback_at)} · ${timesAsked(element.feedback_requests)}`
            : 'No feedback asked for yet'}
        </span>
      </div>
    ) : null}
    {element.moves.length ? <Moves moves={element.moves} /> : null}
  </div>
);

/**
 * "Open latest version": the current version through the staff link route
 * (overview/cells openDocument): a short-lived link made on click, in a tab
 * with no opener, as the Documents drill-down and the version history open
 * files. Nothing when there is no current version.
 */
const OpenLatest = ({ studentId, doc }) => {
  const [error, setError] = useState('');
  const [opening, setOpening] = useState(false);
  if (!doc.tracker_document_id) return null;
  const label = doc.type === 'cv' ? 'CV' : documentTypeLabel(doc.type).toLowerCase();
  const open = async () => {
    setError('');
    setOpening(true);
    try {
      await openDocument(studentId, doc.tracker_document_id);
    } catch (err) {
      setError(openDocumentErrorMessage(err));
    } finally {
      setOpening(false);
    }
  };
  return (
    <div className="td-act-btnrow td-act-gap-sm">
      <button
        type="button"
        className="td-btn td-btn--small"
        onClick={open}
        disabled={opening}
        aria-label={`Open the latest ${label}, version ${doc.current_version}, in a new tab`}
      >
        Open latest version
      </button>
      {error ? <small className="td-doc__error" role="alert">{error}</small> : null}
    </div>
  );
};

const DocumentCard = ({ doc, studentId }) => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyMounted, setHistoryMounted] = useState(false);
  const [axesOpen, setAxesOpen] = useState(false);
  useEffect(() => {
    setHistoryOpen(false);
    setHistoryMounted(false);
    setAxesOpen(false);
  }, [studentId]);
  const label = documentTypeLabel(doc.type) || 'Document';
  const withMoves = doc.elements.filter((e) => e.moves.length || e.feedback_requests);
  const points = Number(doc.history_points) || 0;
  const panelId = `td-act-vh-${doc.type}`;
  const axesId = `td-act-axes-${doc.type}`;
  const elements = doc.elements.map((e) => ({ key: e.key, label: e.label }));
  const toggleHistory = () => {
    if (!points) return;
    setHistoryMounted(true);
    setHistoryOpen((open) => !open);
  };
  return (
    <div className="td-act-docwrap">
      <div className={`td-card td-act-doc${historyOpen ? ' td-act-doc--open' : ''}`}>
        <div className="td-act-doc__head">
          <div className="td-act-doc__main">
            <h4 className="td-card__title">{label}</h4>
            <div className="td-act-meta">{documentMeta(doc)}</div>
          </div>
          <OpenLatest studentId={studentId} doc={doc} />
        </div>
        <div className="td-act-doc__body">
          <div className="td-act-doc__score">
            <h5 className="td-act-doc__label">Score</h5>
            {doc.score?.current != null ? (
              <ScoreReadout score={doc.score} chart chartSize="small" />
            ) : (
              <p className="td-act-note">{NO_FEEDBACK_SCORE}</p>
            )}
          </div>
          <div className="td-act-doc__moves">
            <h5 className="td-act-doc__label">Writing moves in the latest feedback</h5>
            {withMoves.length ? (
              <div className="td-act-elements">
                {withMoves.map((e) => <Element key={e.key} element={e} showHead={doc.elements.length > 1} />)}
              </div>
            ) : <p className="td-act-meta">No writing moves in the latest feedback.</p>}
          </div>
        </div>
        <div className="td-act-doc__foot">
          <button
            type="button"
            className="td-act-vhbtn"
            aria-expanded={axesOpen}
            aria-controls={axesId}
            onClick={() => setAxesOpen((open) => !open)}
          >
            Scoring axes
            <span aria-hidden="true">{axesOpen ? ' ▴' : ' ▾'}</span>
          </button>
          <button
            type="button"
            className="td-act-vhbtn"
            aria-expanded={historyOpen}
            aria-controls={panelId}
            disabled={!points}
            onClick={toggleHistory}
          >
            {`Version history and files · ${points}`}
            {points ? <span aria-hidden="true">{historyOpen ? ' ▴' : ' ▾'}</span> : null}
          </button>
        </div>
      </div>
      <div id={axesId} className="td-act-doc__panel" hidden={!axesOpen}>
        <AxisPanel groups={doc.axes || []} chartSize="small" />
      </div>
      {historyMounted ? (
        <DocumentHistory
          id={panelId}
          hidden={!historyOpen}
          studentId={studentId}
          documentType={doc.type}
          elements={elements}
          initialElement={elements[0]?.key}
        />
      ) : (
        <section id={panelId} className="td-act-doc__panel" hidden aria-label={`${label} version history`} />
      )}
    </div>
  );
};

/**
 * Applications, from GET …/activity/applications: course choices against
 * their entry requirements, and each application document's versions and
 * the writing moves found in the latest draft that got feedback, with a
 * trend sparkline and its version history (DocumentHistory, opened inline
 * from "Version history · N"; `studentId` is whose). "Open latest version"
 * opens the current version (`tracker_document_id`) through the staff link
 * route, like every other file here. "See TMUA practice" moves to Exam practice with
 * `onJump('exams')` (lib/scrollToSection by default). Chat is the last block
 * and keeps its own loading and error state.
 */
const ApplicationsSection = ({
  state, studentId, onJump = scrollToSection, chatState = null,
}) => {
  const data = state.status === 'ready' ? state.data : null;
  return (
    <ActivitySection
      id="applications"
      title="Applications"
      description="Course choices against their entry requirements, and how the student's application documents are developing."
    >
      <SectionState state={state} what="applications" />
      {data ? (
        <>
          <div className="td-act-grid td-act-grid--2">
            <div className="td-card">
              <h4 className="td-card__title">Course choices</h4>
              {data.choices.length
                ? data.choices.map((c) => <Choice key={`${c.institution}-${c.course}`} choice={c} onJump={onJump} />)
                : <p className="td-act-meta">No course choices saved yet.</p>}
              <p className="td-act-note">
                Predicted grades come from the school record. Shown only where the school has that feature on.
              </p>
            </div>
            {data.documents.map((d) => (
              <DocumentCard key={d.type} doc={d} studentId={studentId} />
            ))}
          </div>
          <p className="td-act-note">
            Writing moves (✓ shown, ~ partly, ✗ not yet) are detected by matching phrases in the latest draft the
            student asked feedback on. They&apos;re a guide to what&apos;s there, not a mark.
          </p>
        </>
      ) : null}
      {chatState ? <ChatSection embedded state={chatState} studentId={studentId} /> : null}
    </ActivitySection>
  );
};

export default ApplicationsSection;
